import type { MdTextResponse } from './types'

/** Static route segment; must not match catalog page ids (e.g. `md-text`). */
export const MD_TEXT_VIEWER_ROUTE_SEGMENT = 'md-text-viewer'

export type MdTextViewerLocationState = {
  from?: string
  payload?: Record<string, unknown>
  tileFilterSelections?: Record<string, unknown>[]
  startInEditMode?: boolean
  cachedResponse?: MdTextResponse
}

function viewerPrefix(appKey: string) {
  return `/apps/${appKey}/${MD_TEXT_VIEWER_ROUTE_SEGMENT}/`
}

export function isMdTextViewerBasePath(pathname: string, appKey: string) {
  const pathOnly = pathname.split('?')[0]
  const base = `/apps/${appKey}/${MD_TEXT_VIEWER_ROUTE_SEGMENT}`
  return pathOnly === base || pathOnly === `${base}/`
}

export function isMdTextViewerPath(pathname: string, appKey?: string) {
  if (appKey) {
    const prefix = viewerPrefix(appKey)
    return pathname.startsWith(prefix) && pathname.length > prefix.length
  }
  return new RegExp(`^/apps/[^/]+/${MD_TEXT_VIEWER_ROUTE_SEGMENT}/.+`).test(pathname)
}

export function isMdTextViewerRoute(pathname: string, appKey: string) {
  return isMdTextViewerBasePath(pathname, appKey) || isMdTextViewerPath(pathname, appKey)
}

export function isValidCatalogPageId(pageId?: string) {
  return Boolean(pageId && pageId !== '/' && !pageId.includes('undefined'))
}

export function catalogReturnPathToPageId(fromPath: string, appKey: string): string | undefined {
  const pathOnly = fromPath.split('?')[0]
  const prefix = `/apps/${appKey}/`
  if (!pathOnly.startsWith(prefix)) {
    return undefined
  }
  const pagePath = pathOnly.slice(prefix.length)
  if (!pagePath) {
    return undefined
  }
  return `/${appKey}/${pagePath}`
}

export function buildMdTextViewerPath(appKey: string, contentId: string) {
  const trimmed = contentId.trim()
  if (!trimmed) {
    throw new Error('Cannot build md-text viewer path without a content id')
  }
  const pathSuffix = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed
  return `/apps/${appKey}/${MD_TEXT_VIEWER_ROUTE_SEGMENT}/${pathSuffix}`
}

export function parseMdTextContentIdFromPath(pathname: string, appKey: string): string | undefined {
  const prefix = viewerPrefix(appKey)
  if (!pathname.startsWith(prefix)) {
    return undefined
  }
  const suffix = decodeURIComponent(pathname.slice(prefix.length).split('?')[0])
  if (!suffix) {
    return undefined
  }
  return suffix.startsWith('/') ? suffix : `/${suffix}`
}

export function withSectionSearchParam(search: string, sectionId?: string) {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  if (sectionId) {
    params.set('section', sectionId)
  } else {
    params.delete('section')
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}
